import React, { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  level?: 'page' | 'component' | 'critical'
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  eventId: string | null
}

export class PortalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, level = 'component' } = this.props
    
    // Log to Sentry with context
    const eventId = Sentry.withScope((scope) => {
      scope.setTag('errorBoundary', true)
      scope.setTag('errorLevel', level)
      scope.setContext('errorInfo', errorInfo)
      scope.setContext('componentStack', {
        componentStack: errorInfo.componentStack
      })
      
      if (level === 'critical') {
        scope.setLevel('fatal')
      } else if (level === 'page') {
        scope.setLevel('error')
      } else {
        scope.setLevel('warning')
      }
      
      return Sentry.captureException(error)
    })

    this.setState({
      error,
      errorInfo,
      eventId
    })

    // Call custom error handler
    if (onError) {
      onError(error, errorInfo)
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error Boundary caught an error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({
        eventId: this.state.eventId,
        title: 'Something went wrong',
        subtitle: 'Our team has been notified. You can help us fix this by providing additional details.',
        subtitle2: 'Thank you for helping improve AI Facility Manager!',
        labelName: 'Name',
        labelEmail: 'Email',
        labelComments: 'What happened?',
        labelClose: 'Close',
        labelSubmit: 'Submit Report',
        successMessage: 'Thank you for the report!'
      })
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback, level = 'component' } = this.props
      const { error } = this.state

      // Use custom fallback if provided
      if (fallback) {
        return fallback
      }

      // Different UI based on error level
      if (level === 'critical') {
        return (
          <div className="error-boundary-critical">
            <div className="error-container">
              <div className="error-icon error-icon-critical">
                <span>⚠️</span>
              </div>
              
              <h1 className="error-title">Critical Error</h1>
              
              <p className="error-description">
                AI Facility Manager encountered a critical error and needs to restart. 
                Our team has been automatically notified.
              </p>
              
              {import.meta.env.DEV && (
                <div className="error-debug">
                  <p className="error-debug-message">{error?.message}</p>
                </div>
              )}
              
              <div className="error-actions">
                <button 
                  onClick={this.handleReload}
                  className="btn btn-primary"
                >
                  🔄 Restart Application
                </button>
                
                <button 
                  onClick={this.handleReportFeedback}
                  className="btn btn-secondary"
                >
                  🐛 Report Issue
                </button>
              </div>
            </div>
          </div>
        )
      }

      if (level === 'page') {
        return (
          <div className="error-boundary-page">
            <div className="error-container">
              <div className="error-icon error-icon-warning">
                <span>⚠️</span>
              </div>
              
              <h1 className="error-title">Page Error</h1>
              
              <p className="error-description">
                This page encountered an error. You can try refreshing or return to the dashboard.
              </p>
              
              {import.meta.env.DEV && (
                <div className="error-debug">
                  <p className="error-debug-message">{error?.message}</p>
                </div>
              )}
              
              <div className="error-actions">
                <button 
                  onClick={this.handleReset}
                  className="btn btn-primary"
                >
                  🔄 Try Again
                </button>
                
                <button 
                  onClick={this.handleGoHome}
                  className="btn btn-secondary"
                >
                  🏠 Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        )
      }

      // Component-level error (minimal UI)
      return (
        <div className="error-boundary-component">
          <div className="error-component-content">
            <div className="error-component-icon">⚠️</div>
            <div className="error-component-text">
              <p className="error-component-title">Component Error</p>
              <p className="error-component-subtitle">This component failed to load properly.</p>
            </div>
            <button 
              className="error-component-retry"
              onClick={this.handleReset}
            >
              🔄
            </button>
          </div>
          
          {import.meta.env.DEV && error && (
            <div className="error-component-debug">
              {error.message}
            </div>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

// Higher-order component for easy wrapping
export const withPortalErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <PortalErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </PortalErrorBoundary>
  )
  
  WrappedComponent.displayName = `withPortalErrorBoundary(${Component.displayName || Component.name})`
  
  return WrappedComponent
}